import TableContainer from '@mui/material/TableContainer';
import { Paper } from '@openx/components-core/lib/Paper/Paper.tsx';
import { Table, TableName } from '@openx/components-table/Table.tsx';
import type { AllTableCriteria, CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria.ts';
import type { TableColumn } from '@openx/types/tableHelpers.ts';
import { type JSX, useMemo } from 'react';
import type { DealLibraryItem } from '../../../types/index.ts';
import { ColumnKey, ColumnTitle } from '../constants.ts';
import { useDealLibraryRowActions } from '../hooks/useDealLibraryRowActions.ts';
import { DeviceTypeCellContainer } from './TableCells/DeviceTypeCellContainer.tsx';
import { NameCellContainer } from './TableCells/NameCellContainer.tsx';
import { TextCellContainer } from './TableCells/TextCellContainer.tsx';

type DealLibraryListTableProps = {
  dealTemplates: DealLibraryItem[];
  isLoading: boolean;
  phrase: string;
  criteria: AllTableCriteria;
  onSearchChange: (filters: { phrase: string }) => void;
  onCriteriaChange: (change: CriteriaChange<CriteriaDimension>) => void;
};

export const DealLibraryListTable = ({
  dealTemplates,
  isLoading,
  phrase,
  criteria,
  onSearchChange,
  onCriteriaChange,
}: DealLibraryListTableProps): JSX.Element => {
  const dealLibraryRowActionsCopy = useDealLibraryRowActions();

  const columnDefs: TableColumn<DealLibraryItem>[] = useMemo(
    () => [
      {
        key: ColumnKey.NAME,
        render: ({ tags, id, name, description }) =>
          NameCellContainer({ description, id, name, tags }, phrase, onSearchChange),
        title: ColumnTitle.NAME,
        width: '40%',
        disabled: true,
      },
      {
        key: ColumnKey.DEVICE_TYPE,
        render: ({ device_type }) => DeviceTypeCellContainer({ device_type }),
        title: ColumnTitle.DEVICE_TYPE,
        width: '10%',
      },
      {
        key: ColumnKey.DISTRIBUTION_CHANNEL,
        render: ({ distribution_channel }) =>
          TextCellContainer({ displayValue: distribution_channel, type: ColumnKey.DISTRIBUTION_CHANNEL }),

        title: ColumnTitle.DISTRIBUTION_CHANNEL,
        width: '10%',
      },
      {
        key: ColumnKey.AD_PLACEMENT,
        render: ({ ad_placement }) => TextCellContainer({ displayValue: ad_placement, type: ColumnKey.AD_PLACEMENT }),
        title: ColumnTitle.AD_PLACEMENT,
        width: '10%',
      },
    ],
    [onSearchChange, phrase]
  );

  return (
    <Paper padding="none">
      <TableContainer>
        <Table
          name={TableName.DEAL_LIBRARY}
          data={dealTemplates}
          loading={isLoading}
          columns={columnDefs}
          enableTableHeaderCustomization={false}
          primaryRowAction={dealLibraryRowActionsCopy.CREATE_DEAL}
          criteria={criteria}
          onCriteriaChange={onCriteriaChange}
        />
      </TableContainer>
    </Paper>
  );
};
